// src/pages/NewApplicationForm.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../api/axios';
import './NewApplicationForm.css';
import ApplicationViewPublisher from './ApplicationViewPublisher';

/* ========= Locale helpers ========= */
const LS_LOCALE_KEY = 'locale';
const FALLBACK_LOCALE = 'kz';
const readLocale = () => localStorage.getItem(LS_LOCALE_KEY) || FALLBACK_LOCALE;

function useReactiveLocale(intervalMs = 300) {
  const [locale, setLocale] = useState(readLocale());
  const prevRef = useRef(locale);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_LOCALE_KEY) {
        const next = e.newValue || FALLBACK_LOCALE;
        prevRef.current = next;
        setLocale(next);
      }
    };
    window.addEventListener('storage', onStorage);

    const onCustom = (e) => {
      const next = e?.detail?.locale || readLocale();
      prevRef.current = next;
      setLocale(next);
    };
    window.addEventListener('app:locale-changed', onCustom);

    const id = setInterval(() => {
      const cur = readLocale();
      if (cur !== prevRef.current) {
        prevRef.current = cur;
        setLocale(cur);
      }
    }, intervalMs);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('app:locale-changed', onCustom);
      clearInterval(id);
    };
  }, [intervalMs]);

  return locale;
}

export default function NewApplicationForm({
  prefill,
  lockPrefill = false,
  submitUrl = '/publisher/applications',
  onSuccess,
}) {
  const locale = useReactiveLocale();
  const uiLocale = ['kk', 'kz'].includes(locale) ? 'kz' : 'ru';

  const t = useMemo(
    () =>
      ({
        kz: {
          umk: 'ОӘК',
          category: 'Санат',
          criteriaForm: 'Критерий формасы',
          choose: 'Таңдаңыз',
          section_count: 'Енгізілетін аталымдар саны',
          section_count_preschool: 'Бөлімдер саны',
          authors: 'Авторлар',
          page_count: 'Бет саны',
          sheet_count: 'Баспа табақ',
          duration: 'Ұзақтығы (минут)',
          message: 'Хабарлама',
          section: (n) => `${n}`,
          file_name_ph: 'Файл немесе сілтеме атауы',
          link: 'Сілтеме (электронды нұсқа үшін)',
          file_pick: 'Файл таңдау',
          mode_file: 'Файл жүктеу',
          mode_link: 'Сілтеме',
          section_archive_label: 'Секция архиві (ZIP/RAR/7Z)',
          section_archive_required: 'Сілтеме берілсе, секция архиві міндетті',
          tooltip_sections: 'Әр бөлімге файл атауы және файл немесе электронды сілтеме тіркелуі керек.',
          tooltip_section_file: 'Файл атауын енгізіңіз, файл жүктеңіз немесе сілтеме беріңіз.',
          parent_label: 'Алдыңғы өтініш (жалғасы)',
          parent_required_msg: 'Бұл топ «жалғасы» режимінде. Алдыңғы өтініш (parent) міндетті.',
          parent_loading: 'Жүктелуде...',
          parent_empty: 'Сәйкес алдыңғы Өтінімдер табылмады.',
          parent_open: 'Алдыңғы өтінішті көру',
          prev_app: 'Алдыңғы өтініш',
          close: 'Жабу',
          autofilled: '✅ Алдыңғы өтініштің деректері толтырылды. Архивті жаңадан жүктеңіз.',
          zip_label: 'Өтінімге қатысты құжаттарды жүктеу (ZIP форматта)',
          drag_here: 'Құжатты бірден жүктеңіз немесе таңдаңыз',
          another_file: 'Басқа файл',
          zip_required: 'ZIP архив міндетті',
          zip_type_wrong: 'Қате файл түрі',
          zip_ext_only: 'Тек .zip файл',
          size_le: (n) => `≤ ${n} MB дейін`,
          err_sections_range: 'Бөлім саны дұрыс емес (1–20)',
          err_file_name_required: (n) => `❌ ${n}-бөлім: файл/сілтеме атауы міндетті`,
          err_file_or_link_required: (n) => `❌ ${n}-бөлім: файл немесе сілтеме міндетті`,
          err_link_archive_required: (n) => `❌ ${n}-бөлім: сілтеме берілсе секция архиві міндетті`,
          err_group_required: 'ОӘК таңдау керек',
          err_category_required: 'Санат таңдау керек',
          err_criteria_required: 'Критерий формасын таңдаңыз',
          submit: 'Сақтау',
          saving: 'Сақталуда...',
          success: '✅ Өтініш сәтті сақталды!',
          error: '❌ Қате орын алды. Форманы тексеріңіз.',
          loadingShort: 'Жүктелуде...',
          // ✅ Word файл
          word_label: 'Жауап құжатын салу (DOCX)',
          word_hint: 'Доработкаға жауап ретінде Word құжатын жүктеңіз',
          word_pick: 'DOCX файл таңдау',
          word_uploading: 'Жүктелуде...',
          word_uploaded: '✅ Word файл жүктелді',
          word_upload_error: '❌ Word жүктеу қатесі',
          word_download: '⬇ Word жүктеп алу',
          word_replace: 'Ауыстыру',
          word_size_error: 'Файл тым үлкен (max 50MB)',
          word_type_error: 'Тек .doc немесе .docx форматы',
        },
        ru: {
          umk: 'УМК',
          category: 'Категория',
          criteriaForm: 'Форма критериев',
          choose: 'Выберите',
          section_count: 'Количество вводимых наименований',
          section_count_preschool: 'Количество разделов',
          authors: 'Авторы',
          page_count: 'Количество страниц',
          sheet_count: 'Печатный лист',
          duration: 'Продолжительность (мин)',
          message: 'Сообщение',
          section: (n) => `${n}`,
          file_name_ph: 'Название файла или ссылки',
          link: 'Ссылка (для электронной версии)',
          file_pick: 'Выбрать файл',
          mode_file: 'Загрузить файл',
          mode_link: 'Ссылка',
          section_archive_label: 'Архив секции (ZIP/RAR/7Z)',
          section_archive_required: 'При указании ссылки архив секции обязателен',
          tooltip_sections: 'Для каждого раздела нужно указать название и добавить файл или ссылку.',
          tooltip_section_file: 'Введите название, загрузите файл или укажите ссылку.',
          parent_label: 'Предыдущая заявка (продолжение)',
          parent_required_msg: 'Эта группа в режиме «продолжение». Предыдущая заявка обязательна.',
          parent_loading: 'Загрузка...',
          parent_empty: 'Подходящих предыдущих заявок не найдено.',
          parent_open: 'Открыть предыдущую заявку',
          prev_app: 'Предыдущая заявка',
          close: 'Закрыть',
          autofilled: '✅ Данные предыдущей заявки заполнены. Загрузите новый архив.',
          zip_label: 'Загрузить документы к заявке (в формате ZIP)',
          drag_here: 'Перетащите документ или выберите',
          another_file: 'Другой файл',
          zip_required: 'ZIP архив обязателен',
          zip_type_wrong: 'Неверный тип файла',
          zip_ext_only: 'Только .zip файлы',
          size_le: (n) => `≤ ${n} MB`,
          err_sections_range: 'Некорректное число разделов (1–20)',
          err_file_name_required: (n) => `❌ Раздел ${n}: название обязательно`,
          err_file_or_link_required: (n) => `❌ Раздел ${n}: файл или ссылка обязательны`,
          err_link_archive_required: (n) => `❌ Раздел ${n}: при указании ссылки архив секции обязателен`,
          err_group_required: 'Выберите УМК',
          err_category_required: 'Выберите категорию',
          err_criteria_required: 'Выберите форму критериев',
          submit: 'Сохранить',
          saving: 'Загрузка...',
          success: '✅ Заявка успешно сохранена!',
          error: '❌ Произошла ошибка. Проверьте форму.',
          loadingShort: 'Загрузка...',
          // ✅ Word файл
          word_label: 'Загрузить ответный документ (DOCX)',
          word_hint: 'Загрузите Word-документ как ответ на доработку',
          word_pick: 'Выбрать DOCX файл',
          word_uploading: 'Загрузка...',
          word_uploaded: '✅ Word файл загружен',
          word_upload_error: '❌ Ошибка загрузки Word',
          word_download: '⬇ Скачать Word',
          word_replace: 'Заменить',
          word_size_error: 'Файл слишком большой (max 50MB)',
          word_type_error: 'Только .doc или .docx формат',
        },
      })[uiLocale],
    [uiLocale]
  );

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [groups, setGroups] = useState([]);
  const [criteriaForms, setCriteriaForms] = useState([]);
  const [criteriaLoading, setCriteriaLoading] = useState(false);

  const [parentApps, setParentApps] = useState([]);
  const [parentLoading, setParentLoading] = useState(false);
  const [parentError, setParentError] = useState('');
  const [showParentModal, setShowParentModal] = useState(false);
  const [autofilledInfo, setAutofilledInfo] = useState('');

  // ✅ Word файл state
  const [wordFile, setWordFile] = useState(null);
  const [wordUploading, setWordUploading] = useState(false);
  const [wordUploaded, setWordUploaded] = useState(false);
  const [wordUploadedName, setWordUploadedName] = useState('');
  const [wordError, setWordError] = useState('');
  const [savedApplicationId, setSavedApplicationId] = useState(null);
  const wordInputRef = useRef(null);

  const [form, setForm] = useState({
    group_id: prefill?.group_id ? String(prefill.group_id) : '',
    category_id: prefill?.category_id ? String(prefill.category_id) : '',
    criteria_form_id: '',
    section_count: '',
    authors: '',
    page_count: '',
    sheet_count: '',
    duration_minutes: '',
    message: '',
    parent_application_id: '',
  });

  const [fileNames, setFileNames] = useState({});
  const [files, setFiles] = useState({});
  const [links, setLinks] = useState({});
  const [sectionModes, setSectionModes] = useState({});
  const [sectionArchives, setSectionArchives] = useState({});

  const [archive, setArchive] = useState(null);
  const [archiveError, setArchiveError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const zipInputRef = useRef(null);

  const MAX_ZIP_MB = 100;
  const MAX_WORD_MB = 50;

  useEffect(() => {
    if (!prefill) return;
    setForm((p) => ({
      ...p,
      group_id: prefill.group_id != null ? String(prefill.group_id) : p.group_id,
      category_id: prefill.category_id != null ? String(prefill.category_id) : p.category_id,
    }));
  }, [prefill?.group_id, prefill?.category_id]);

  // Группалар жүктеу
  useEffect(() => {
    const headers = { 'Accept-Language': locale };
    let alive = true;

    axios
      .get('/publisher/groups', { headers })
      .then((res) => {
        if (!alive) return;
        const data = res.data?.data || res.data || [];
        setGroups(Array.isArray(data) ? data : []);
        if (prefill?.group_id) {
          setForm((p) => ({ ...p, group_id: String(prefill.group_id) }));
        }
      })
      .catch(() => { if (!alive) return; setGroups([]); });

    return () => { alive = false; };
  }, [locale]);

  // Критерий формалары
  useEffect(() => {
    let alive = true;
    const headers = { 'Accept-Language': locale };
    setCriteriaLoading(true);

    axios
      .get('/publisher/ref/criteria-forms', { headers })
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCriteriaForms(Array.isArray(list) ? list : []);
      })
      .catch(() => { if (!alive) return; setCriteriaForms([]); })
      .finally(() => { if (!alive) return; setCriteriaLoading(false); });

    return () => { alive = false; };
  }, [locale]);

  useEffect(() => {
    if (!criteriaForms || criteriaForms.length !== 1) return;
    setForm((p) => (p.criteria_form_id ? p : { ...p, criteria_form_id: String(criteriaForms[0].id) }));
  }, [criteriaForms]);

  const selectedGroup = groups.find((g) => Number(g?.id) === Number(form.group_id));
  const educationLevelId = Number(selectedGroup?.education_level_id ?? selectedGroup?.education_level?.id ?? 0) || 0;
  const isPreschoolEducation = educationLevelId === 1;
  const hasSelectedGroupId = !!form.group_id;
  const isGroupResolved = !hasSelectedGroupId || !!selectedGroup;

  const sectionCountLabel = !isGroupResolved
    ? t.loadingShort
    : isPreschoolEducation
      ? (t.section_count_preschool || t.section_count)
      : t.section_count;

  const mode = selectedGroup?.continuation?.mode ?? selectedGroup?.continuation_mode ?? 'new';
  const isContinuationGroup = mode === 'continuation';
  const isMixedGroup = mode === 'mixed';

  // ✅ Доработка тобы ма?
  const isDorabotka = isContinuationGroup || isMixedGroup;

  const selectedCategory = (selectedGroup?.categories || []).find(
    (c) => Number(c?.id) === Number(form.category_id)
  );
  const selectedCategoryName = selectedCategory?.[`name_${uiLocale}`] || selectedCategory?.name || '';
  const sectionCount = Math.min(20, Math.max(0, parseInt(form.section_count || 0, 10) || 0));

  // Section атауларын автоматты толтыру
  useEffect(() => {
    if (isPreschoolEducation) return;
    if (!selectedCategoryName || sectionCount < 1) return;

    setFileNames((prev) => {
      const next = { ...prev };
      let changed = false;
      for (let i = 0; i < sectionCount; i++) {
        const current = String(prev[i] || '').trim();
        if (!current) {
          next[i] = `${selectedCategoryName} ${i + 1}`;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedCategoryName, sectionCount, isPreschoolEducation]);

  // Section саны азайғанда тазалау
  useEffect(() => {
    const clean = (setter) => setter((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((k) => {
        const idx = Number(k);
        if (Number.isFinite(idx) && idx >= sectionCount) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    clean(setFileNames);
    clean(setFiles);
    clean(setLinks);
    clean(setSectionModes);
    clean(setSectionArchives);
  }, [sectionCount]);

  // Алдыңғы өтінімдерді топ арқылы алу
  useEffect(() => {
    if (!selectedGroup || !(isContinuationGroup || isMixedGroup)) {
      setParentApps([]);
      setParentError('');
      setForm((p) => ({ ...p, parent_application_id: '' }));
      setAutofilledInfo('');
      return;
    }

    setParentLoading(true);
    setParentError('');
    setParentApps([]);

    axios
      .get(`/publisher/groups/${selectedGroup.id}/parent-applications`, {
        headers: { 'Accept-Language': locale },
      })
      .then((res) => {
        const list = res.data?.data || [];
        setParentApps(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || '';
        setParentError(msg || (uiLocale === 'kz'
          ? 'Алдыңғы өтінімдерді жүктеу сәтсіз'
          : 'Не удалось загрузить предыдущие заявки'));
      })
      .finally(() => setParentLoading(false));
  }, [selectedGroup?.id, locale, isContinuationGroup, isMixedGroup]);

  // Өтініш таңдалғанда деректерді автоматты толтыру
  const handleParentApplicationSelect = (appId) => {
    setForm((p) => ({ ...p, parent_application_id: appId }));

    if (!appId) {
      setAutofilledInfo('');
      return;
    }

    const parentApp = parentApps.find((a) => String(a.id) === String(appId));
    if (!parentApp) return;

    setForm((p) => ({
      ...p,
      parent_application_id: appId,
      category_id: parentApp.category_id ? String(parentApp.category_id) : p.category_id,
      criteria_form_id: parentApp.criteria_form_id ? String(parentApp.criteria_form_id) : p.criteria_form_id,
      section_count: parentApp.section_count ? String(parentApp.section_count) : p.section_count,
      authors: parentApp.authors || p.authors,
      page_count: parentApp.page_count ? String(parentApp.page_count) : p.page_count,
      sheet_count: parentApp.sheet_count ? String(parentApp.sheet_count) : p.sheet_count,
      duration_minutes: parentApp.duration_minutes ? String(parentApp.duration_minutes) : p.duration_minutes,
      message: parentApp.message || p.message,
    }));

    setArchive(null);
    setArchiveError('');
    if (zipInputRef.current) zipInputRef.current.value = '';

    setFiles({});
    setLinks({});
    setSectionArchives({});
    setSectionModes({});
    setFileNames({});

    // Word файлды да тазалаймыз
    setWordFile(null);
    setWordUploaded(false);
    setWordUploadedName('');
    setWordError('');
    setSavedApplicationId(null);
    if (wordInputRef.current) wordInputRef.current.value = '';

    setAutofilledInfo(t.autofilled);
  };

  // Parent modal ESC + scroll-lock
  useEffect(() => {
    if (!showParentModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowParentModal(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev || '';
    };
  }, [showParentModal]);

  const parentIdNum = Number(form.parent_application_id) || null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const setSectionMode = (idx, newMode) => {
    setSectionModes((p) => ({ ...p, [idx]: newMode }));
    if (newMode === 'file') {
      setLinks((p) => { const n = { ...p }; delete n[idx]; return n; });
      setSectionArchives((p) => { const n = { ...p }; delete n[idx]; return n; });
    } else {
      setFiles((p) => { const n = { ...p }; delete n[idx]; return n; });
    }
  };

  const getSectionMode = (idx) => sectionModes[idx] || 'file';

  const formatBytes = (b) => {
    if (b === 0) return '0 B';
    if (!b) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(b) / Math.log(k));
    return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const validateZip = (file) => {
    if (!file) return { ok: false, err: t.zip_required };
    const extOk = /\.zip$/i.test(file.name);
    if (!extOk) return { ok: false, err: t.zip_ext_only };
    const sizeOk = file.size <= MAX_ZIP_MB * 1024 * 1024;
    if (!sizeOk) return { ok: false, err: t.size_le(MAX_ZIP_MB) };
    return { ok: true };
  };

  const validateWord = (file) => {
    if (!file) return { ok: false, err: t.word_type_error };
    const extOk = /\.(doc|docx)$/i.test(file.name);
    if (!extOk) return { ok: false, err: t.word_type_error };
    const sizeOk = file.size <= MAX_WORD_MB * 1024 * 1024;
    if (!sizeOk) return { ok: false, err: t.word_size_error };
    return { ok: true };
  };

  const handleArchiveChange = (eOrFile) => {
    setArchiveError('');
    const file = eOrFile?.target ? eOrFile.target.files?.[0] : eOrFile;
    if (!file) return;
    const { ok, err } = validateZip(file);
    if (!ok) {
      setArchive(null);
      setArchiveError(err);
      if (zipInputRef.current) zipInputRef.current.value = '';
      return;
    }
    setArchive(file);
  };

  const clearArchive = () => {
    setArchive(null);
    setArchiveError('');
    if (zipInputRef.current) zipInputRef.current.value = '';
  };

  const onDropZip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleArchiveChange(file);
  };

  // ✅ Word файл жүктеу (өтініш сақталған соң)
  const handleWordUpload = async (file) => {
    if (!file) return;

    const { ok, err } = validateWord(file);
    if (!ok) {
      setWordError(err);
      if (wordInputRef.current) wordInputRef.current.value = '';
      return;
    }

    if (!savedApplicationId) {
      setWordError(
        uiLocale === 'kz'
          ? '❌ Алдымен өтінішті сақтаңыз'
          : '❌ Сначала сохраните заявку'
      );
      return;
    }

    setWordError('');
    setWordUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `/publisher/applications/${savedApplicationId}/upload-word`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setWordFile(file);
      setWordUploaded(true);
      setWordUploadedName(file.name);
    } catch (err) {
      const msg = err?.response?.data?.message || t.word_upload_error;
      setWordError(msg);
    } finally {
      setWordUploading(false);
    }
  };

  // ✅ Word жүктеп алу
  const handleWordDownload = async () => {
    if (!savedApplicationId) return;

    try {
      const res = await axios.get(
        `/publisher/applications/${savedApplicationId}/download-word`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', wordUploadedName || `application_${savedApplicationId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Word download error:', err);
    }
  };

  const validateBeforeSubmit = () => {
    if (!form.group_id) return t.err_group_required;
    if (!form.category_id) return t.err_category_required;
    if (!form.criteria_form_id) return t.err_criteria_required;
    if (sectionCount < 1 || sectionCount > 20) return t.err_sections_range;
    if (isContinuationGroup && !form.parent_application_id) return t.parent_required_msg;

    const z = validateZip(archive);
    if (!z.ok) return z.err || t.zip_required;

    for (let i = 0; i < sectionCount; i++) {
      const n = i + 1;
      const name = (fileNames[i] || '').trim();
      if (!name) return t.err_file_name_required(n);

      const secMode = getSectionMode(i);
      if (secMode === 'file') {
        if (!files[i]) return t.err_file_or_link_required(n);
      } else {
        const hasLink = !!(links[i] || '').trim();
        if (!hasLink) return t.err_file_or_link_required(n);
        if (!sectionArchives[i]) return t.err_link_archive_required(n);
      }
    }

    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    setArchiveError('');
    setLoading(true);

    const preErr = validateBeforeSubmit();
    if (preErr) {
      if (preErr === t.zip_required || preErr === t.zip_ext_only || preErr === t.zip_type_wrong || preErr.includes('MB')) {
        setArchiveError(preErr);
      } else {
        setError(preErr);
      }
      setLoading(false);
      return;
    }

    const data = new FormData();

    data.append('application_group_id', form.group_id);
    data.append('category_id', form.category_id);
    data.append('criteria_form_id', form.criteria_form_id);
    data.append('section_count', String(sectionCount));

    if (form.authors) data.append('authors', form.authors);
    if (form.page_count) data.append('page_count', form.page_count);
    if (form.sheet_count) data.append('sheet_count', form.sheet_count);
    if (form.duration_minutes) data.append('duration_minutes', form.duration_minutes);
    if (form.message) data.append('message', form.message);
    if (form.parent_application_id) data.append('parent_application_id', form.parent_application_id);

    if (archive) data.append('archive', archive);

    for (let i = 0; i < sectionCount; i++) {
      const n = i + 1;
      const name = (fileNames[i] || '').trim();
      const secMode = getSectionMode(i);

      data.append(`files[${i}][section_number]`, String(n));
      data.append(`files[${i}][name]`, name);

      if (secMode === 'file') {
        const f = files[i];
        if (f) data.append(`files[${i}][file]`, f);
      } else {
        const link = (links[i] || '').trim();
        if (link) data.append(`files[${i}][file_link]`, link);
        const sa = sectionArchives[i];
        if (sa) data.append(`files[${i}][section_archive]`, sa);
      }
    }

    try {
      const res = await axios.post(submitUrl, data, {
        headers: { 'Accept-Language': locale },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      // ✅ Сақталған өтінімнің ID-ін алу
      const appId = res.data?.application?.id;
      if (appId) {
        setSavedApplicationId(appId);
      }

      setSuccess(t.success);
      setError('');
      setAutofilledInfo('');

      // Word файл бар болса — бірден жүктейміз
      if (isDorabotka && wordFile && appId) {
        const wordFormData = new FormData();
        wordFormData.append('file', wordFile);
        try {
          await axios.post(
            `/publisher/applications/${appId}/upload-word`,
            wordFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          setWordUploaded(true);
        } catch (wordErr) {
          console.error('Word upload error:', wordErr);
        }
      }

      onSuccess?.();
    } catch (err) {
      console.error('STORE ERROR:', err?.response?.status, err?.response?.data || err?.message);
      const errors = err?.response?.data?.errors;
      const msg = err?.response?.data?.message;
      setError(errors ? JSON.stringify(errors, null, 2) : (msg || t.error));
    } finally {
      setLoading(false);
    }
  };

  const renderSelect = (name, label, options, isGroup = false, disabled = false) => (
    <div className="form-group">
      <label>{label}</label>
      <select name={name} value={form[name]} onChange={handleChange} disabled={disabled} required>
        <option value="">{t.choose}</option>
        {(options || []).map((item) => (
          <option key={item.id} value={item.id}>
            {isGroup ? item.title : (item[`name_${uiLocale}`] || item.name)}
          </option>
        ))}
      </select>
    </div>
  );

  const renderCriteriaSelect = () => (
    <div className="form-group">
      <label>{t.criteriaForm}</label>
      <select
        name="criteria_form_id"
        value={form.criteria_form_id}
        onChange={handleChange}
        required
        disabled={criteriaLoading}
      >
        <option value="">{criteriaLoading ? t.loadingShort : t.choose}</option>
        {criteriaForms.map((c) => (
          <option key={c.id} value={c.id}>
            {c?.[`name_${uiLocale}`] || c?.name || `#${c.id}`}
          </option>
        ))}
      </select>
    </div>
  );

  const renderSection = (i) => {
    const secMode = getSectionMode(i);
    const isLink = secMode === 'link';

    return (
      <div key={i} className="form-group section-row">
        <label className="label-inline">
          {t.section(i + 1)}
          <div className="info-tooltip-wrapper" aria-hidden="true">
            <div className="info-icon">i</div>
            <div className="tooltip-bubble">{t.tooltip_section_file}</div>
          </div>
        </label>

        <div className="stack-vert">
          <input
            className="input"
            type="text"
            placeholder={t.file_name_ph}
            value={fileNames[i] || ''}
            onChange={(e) => setFileNames((p) => ({ ...p, [i]: e.target.value }))}
            required
          />

          <div className="section-mode-toggle">
            <button
              type="button"
              className={`section-mode-btn ${!isLink ? 'section-mode-btn--active' : ''}`}
              onClick={() => setSectionMode(i, 'file')}
            >
              📄 {t.mode_file}
            </button>
            <button
              type="button"
              className={`section-mode-btn ${isLink ? 'section-mode-btn--active' : ''}`}
              onClick={() => setSectionMode(i, 'link')}
            >
              🔗 {t.mode_link}
            </button>
          </div>

          {!isLink && (
            <div className="section-file-row">
              <input
                className="input"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFiles((p) => ({ ...p, [i]: f }));
                }}
              />
              {files[i] && (
                <span className="section-file-hint">{files[i].name} ({formatBytes(files[i].size)})</span>
              )}
            </div>
          )}

          {isLink && (
            <>
              <input
                className="input"
                type="url"
                placeholder={t.link}
                value={links[i] || ''}
                onChange={(e) => setLinks((p) => ({ ...p, [i]: e.target.value }))}
                required
              />
              <div className="section-archive-block">
                <label className="section-archive-label">
                  {t.section_archive_label}
                  <span className="required-mark"> *</span>
                </label>
                <input
                  className="input"
                  type="file"
                  accept=".zip,.rar,.7z"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setSectionArchives((p) => ({ ...p, [i]: f }));
                  }}
                />
                {sectionArchives[i] && (
                  <span className="section-file-hint">
                    {sectionArchives[i].name} ({formatBytes(sectionArchives[i].size)})
                  </span>
                )}
                <p className="section-archive-hint">{t.section_archive_required}</p>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ✅ Word файл блогы — тек доработка үшін
  const renderWordUpload = () => {
    if (!isDorabotka) return null;

    return (
      <div className="form-group" style={{ marginTop: '1.5rem' }}>
        <label style={{ fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>
          {t.word_label}
        </label>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
          {t.word_hint}
        </p>

        {/* Файл жүктелмеген */}
        {!wordFile && !wordUploaded && (
          <div>
            <input
              ref={wordInputRef}
              type="file"
              accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (!f) return;
                const { ok, err } = validateWord(f);
                if (!ok) {
                  setWordError(err);
                  if (wordInputRef.current) wordInputRef.current.value = '';
                  return;
                }
                setWordError('');
                setWordFile(f);
              }}
            />
            <button
              type="button"
              onClick={() => wordInputRef.current?.click()}
              style={{
                padding: '0.5rem 1.2rem',
                backgroundColor: '#f3f4f6',
                border: '1px dashed #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                color: '#374151',
              }}
            >
              📎 {t.word_pick}
            </button>
          </div>
        )}

        {/* Файл таңдалған бірақ жүктелмеген */}
        {wordFile && !wordUploaded && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.6rem 1rem',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>📄</span>
            <span style={{ flex: 1, fontSize: '0.9rem', color: '#1e40af' }}>
              {wordFile.name} ({formatBytes(wordFile.size)})
            </span>
            <button
              type="button"
              onClick={() => {
                setWordFile(null);
                setWordError('');
                if (wordInputRef.current) wordInputRef.current.value = '';
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '1.1rem',
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Файл жүктелді */}
        {wordUploaded && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.6rem 1rem',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '6px',
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>✅</span>
            <span style={{ flex: 1, fontSize: '0.9rem', color: '#166534' }}>
              {wordUploadedName || t.word_uploaded}
            </span>
            {savedApplicationId && (
              <button
                type="button"
                onClick={handleWordDownload}
                style={{
                  padding: '0.3rem 0.8rem',
                  backgroundColor: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                {t.word_download}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setWordUploaded(false);
                setWordFile(null);
                setWordUploadedName('');
                if (wordInputRef.current) wordInputRef.current.value = '';
              }}
              style={{
                padding: '0.3rem 0.8rem',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                color: '#374151',
              }}
            >
              {t.word_replace}
            </button>
          </div>
        )}

        {wordUploading && (
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>
            {t.word_uploading}
          </p>
        )}

        {wordError && (
          <p style={{ fontSize: '0.85rem', color: '#dc2626', marginTop: '0.5rem' }}>
            {wordError}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit} encType="multipart/form-data" className="new-application-form">

        {/* Group */}
        {renderSelect('group_id', t.umk, groups, true, lockPrefill && !!prefill?.group_id)}

        {/* Category */}
        {renderSelect(
          'category_id',
          t.category,
          selectedGroup?.categories || [],
          false,
          lockPrefill && !!prefill?.category_id
        )}

        {/* Criteria form */}
        {renderCriteriaSelect()}

        {/* Parent — continuation/mixed топ үшін */}
        {(isContinuationGroup || isMixedGroup) && (
          <div className="form-group select-field">
            <label>
              {t.parent_label}
              {isContinuationGroup && <span className="required-mark"> *</span>}
            </label>

            {parentLoading ? (
              <div className="helper-text">{t.parent_loading}</div>
            ) : parentError ? (
              <div className="error-message">{parentError}</div>
            ) : parentApps.length === 0 ? (
              <div className="info-message helper-text">{t.parent_empty}</div>
            ) : (
              <div className="select-control">
                <select
                  name="parent_application_id"
                  value={form.parent_application_id || ''}
                  onChange={(e) => handleParentApplicationSelect(e.target.value)}
                  required={isContinuationGroup}
                >
                  <option value="">{t.choose}</option>
                  {parentApps.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.category?.[`name_${uiLocale}`] || a.category?.name_kz || `#${a.id}`}
                      {a.average_score ? ` • ${a.average_score}` : ''}
                      {a.verdict ? ` • ${a.verdict}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {autofilledInfo && (
              <div
                style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '6px',
                  color: '#166534',
                  fontSize: '0.875rem',
                }}
              >
                {autofilledInfo}
              </div>
            )}

            {form.parent_application_id && (
              <div className="actions-inline">
                <button type="button" className="btn btn--link" onClick={() => setShowParentModal(true)}>
                  {t.parent_open}
                </button>
              </div>
            )}
          </div>
        )}

        {/* section_count */}
        <div className="form-group">
          <div className="input-with-adornment">
            <input
              className="input"
              name="section_count"
              type="number"
              min="1"
              max="20"
              value={form.section_count}
              onChange={handleChange}
              placeholder={sectionCountLabel}
              required
            />
            <div className="info-tooltip-wrapper" aria-hidden="true">
              <div className="info-icon">i</div>
              <div className="tooltip-bubble">{t.tooltip_sections}</div>
            </div>
          </div>
        </div>

        {/* Optional fields */}
        <input className="input" name="authors" value={form.authors} onChange={handleChange} placeholder={t.authors} />
        <input className="input" name="page_count" type="number" min="1" value={form.page_count} onChange={handleChange} placeholder={t.page_count} />
        <input className="input" name="sheet_count" type="number" min="1" value={form.sheet_count} onChange={handleChange} placeholder={t.sheet_count} />
        <input className="input" name="duration_minutes" type="number" min="1" value={form.duration_minutes} onChange={handleChange} placeholder={t.duration} />

        {/* Sections */}
        {Array.from({ length: sectionCount }).map((_, i) => renderSection(i))}

        {/* Message */}
        <label className="label-block">{t.message}</label>
        <textarea
          className="textarea"
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder={t.message}
        />

        {/* ZIP архив */}
        <div className="form-group">
          <label>
            {t.zip_label}
            <span className="required-mark"> *</span>
          </label>

          <div
            className={`dropzone ${isDragging ? 'dropzone--drag' : ''} ${archiveError ? 'dropzone--error' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={onDropZip}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                zipInputRef.current?.click();
              }
            }}
            aria-label="Загрузка ZIP файла"
          >
            <input
              ref={zipInputRef}
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              onChange={handleArchiveChange}
              hidden
            />

            {!archive ? (
              <div className="dropzone__empty">
                <p className="dropzone__title">{t.drag_here}</p>
                <p className="dropzone__sub">{t.size_le(MAX_ZIP_MB)}</p>
                <button type="button" className="dropzone__choose" onClick={() => zipInputRef.current?.click()}>
                  {t.file_pick}
                </button>
              </div>
            ) : (
              <div className="dropzone__filled">
                <div className="chip">
                  <span className="chip__name">{archive.name}</span>
                  <span className="chip__size">• {formatBytes(archive.size)}</span>
                  <button type="button" className="chip__clear" onClick={clearArchive} aria-label="Очистить">×</button>
                </div>
                <button type="button" className="dropzone__choose" onClick={() => zipInputRef.current?.click()}>
                  {t.another_file}
                </button>
              </div>
            )}
          </div>

          {(archiveError || error) && (
            <div className="zip-errors">
              {archiveError && <div className="zip-error">{archiveError}</div>}
              {error && <div className="zip-error" style={{ whiteSpace: 'pre-wrap' }}>{error}</div>}
            </div>
          )}
        </div>

        {/* ✅ Word файл — тек доработка үшін */}
        {renderWordUpload()}

        <button type="submit" disabled={loading} className="submit-button">
          {loading ? (
            <>
              <span className="spinner-small"></span>
              <span className="ml-8">{t.saving}</span>
            </>
          ) : (
            t.submit
          )}
        </button>

        {success && <p className="success-message">{success}</p>}
      </form>

      {/* Parent view modal */}
      {showParentModal && parentIdNum && (
        <div className="parent-modal-overlay" role="dialog" aria-modal="true" aria-label={t.prev_app}>
          <div className="parent-modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="parent-modal-header">
              <h3 className="parent-modal-title">{t.prev_app}</h3>
              <button className="parent-modal-close" onClick={() => setShowParentModal(false)} aria-label={t.close}>×</button>
            </div>
            <div className="parent-modal-body">
              <ApplicationViewPublisher
                applicationId={parentIdNum}
                onClose={() => setShowParentModal(false)}
                locale={['kk', 'kz'].includes(locale) ? 'kk' : 'ru'}
                embedded={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}